// =============================================================================
// AssignPersonalBookingModal.tsx — T-249
// =============================================================================

import { useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Send, AlertTriangle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Button, Field, Input, Select, Textarea, Spinner } from "@/components/ui";

interface Props {
  assigneeEmployeeId?: string;
  assigneeName?: string;
  // yyyy-MM-dd. James, 21 Aug 2026: "would love a way to click a day and
  // schedule not just a crew but an employee to a task and see it here." Clicking
  // an empty day on the schedule opens this with that day already filled in, so
  // the click is the date rather than the start of retyping it.
  defaultDate?: string;
  // Mig 400 — the LAST day when a run of days was swept on the schedule
  // (James, 29 Sep: "a depot day can only be placed one day at a time").
  // End lands at knock-off on that day; the row is one booking spanning it.
  defaultEndDate?: string;
  onClose: () => void;
  onCreated: () => void;
}

interface EmployeeOpt {
  employee_id: string;
  first_name: string;
  last_name: string;
}

interface ProjectOpt {
  project_id: string;
  project_code: string;
  project_name: string;
}

const ACTIVE_PROJECT_STATUSES = ["scheduled", "mobilising", "live", "acceptance_pending"] as const;

export default function AssignPersonalBookingModal({
  assigneeEmployeeId, assigneeName, defaultDate, defaultEndDate, onClose, onCreated,
}: Props) {
  const user = useRequireAuth();
  const qc = useQueryClient();

  const pinned = !!assigneeEmployeeId;

  // James, 22 Aug 2026: "add an option to select multiple staff - not a full
  // crew, but say a random group of employees have to go do training or pick up
  // some materials. Can be one, could be multiple." So the picker is a checkbox
  // list now: same form, one task row created PER person, and each person gets
  // their own bell and their own bar on the schedule. Deliberately NOT a shared
  // task record - "mark it done" then means done for THAT person, which is what
  // a training day needs.
  const [pickedIds, setPickedIds] = useState<Set<string>>(new Set());
  function togglePicked(id: string) {
    setPickedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  // Mig 356 — a depot day is the same booking with kind='depot' (James, 5 Sep:
  // "Ferdy is in the depot on the 7th and 8th going over future builds…
  // currently just an ADHOC option"). The toggle changes what the form means:
  // depot pre-fills its own title and drops the project — a depot day is by
  // definition not on a job.
  // A swept run of days is almost always depot time (that is the case James
  // raised), so a multi-day open starts on Depot day; one day starts on task
  // as before. Either way the toggle is right there.
  const multiDay = !!defaultEndDate && !!defaultDate && defaultEndDate !== defaultDate;
  const [kind, setKind] = useState<"task" | "depot">(multiDay ? "depot" : "task");
  function pickKind(k: "task" | "depot") {
    setKind(k);
    // Only swap the title while it is still the untouched default of either
    // mode — never stomp something the user typed.
    setTitle(t => (t.trim() === "" || t === "Depot day") ? (k === "depot" ? "Depot day" : "") : t);
    if (k === "depot") setProjectId("");
  }
  const [title, setTitle] = useState(multiDay ? "Depot day" : "");
  // 07:00 because that is when a Coretel day starts, not midnight - a task
  // seeded at 00:00 reads as "some time that day" and has to be corrected every
  // single time.
  const [scheduledStart, setScheduledStart] = useState(defaultDate ? `${defaultDate}T07:00` : "");
  const [scheduledEnd, setScheduledEnd] = useState(
    defaultEndDate ? `${defaultEndDate}T15:30` : defaultDate ? `${defaultDate}T15:30` : "");
  const [locationAddress, setLocationAddress] = useState("");
  const [projectId, setProjectId] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const resolvedAssigneeIds: string[] = pinned ? [assigneeEmployeeId!] : [...pickedIds];

  const { data: employees = [], isLoading: empLoading } = useQuery({
    queryKey: ["personal_booking_assignable_employees"],
    enabled: !pinned,
    queryFn: async (): Promise<EmployeeOpt[]> => {
      const { data, error } = await supabase
        .from("employees")
        .select("employee_id, first_name, last_name")
        .eq("is_active", true)
        // Migration 325 - was .neq("employment_type", "subcontractor"), the same
        // hard-coded rule that lived in EmployeeDetailScreen. It is a column now,
        // seeded from exactly that rule, and settable per person.
        .eq("is_schedulable", true)
        .order("first_name");
      if (error) throw error;
      return (data ?? []) as EmployeeOpt[];
    },
  });

  const { data: projects = [] } = useQuery({
    queryKey: ["personal_booking_active_projects"],
    queryFn: async (): Promise<ProjectOpt[]> => {
      const { data, error } = await supabase
        .from("projects")
        .select("project_id, project_code, project_name")
        .in("status", ACTIVE_PROJECT_STATUSES)
        .order("project_code");
      if (error) throw error;
      return (data ?? []) as ProjectOpt[];
    },
  });

  const createMut = useMutation({
    mutationFn: async () => {
      // One row per person, one insert statement - all land or none do.
      const rows = resolvedAssigneeIds.map(id => ({
        assignee_employee_id: id,
        title: title.trim(),
        scheduled_start: new Date(scheduledStart).toISOString(),
        scheduled_end: scheduledEnd ? new Date(scheduledEnd).toISOString() : null,
        location_address: locationAddress.trim() || null,
        project_id: kind === "depot" ? null : (projectId || null),
        notes: notes.trim() || null,
        booking_kind: kind,                        // mig 356
        created_by: user?.user_id ?? null,
      }));
      const { error } = await supabase.from("personal_bookings").insert(rows as any);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["my_week_personal_bookings"] });
      onCreated();
    },
    onError: (e: any) => setErr(e?.message ?? String(e)),
  });

  const canSubmit = useMemo(
    () => !!(title.trim() && scheduledStart && resolvedAssigneeIds.length > 0) && !createMut.isPending,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [title, scheduledStart, resolvedAssigneeIds.length, createMut.isPending],
  );

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={createMut.isPending ? undefined : onClose}
    >
      <div
        className="bg-ink-card border border-line rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-line">
          <h2 className="font-semibold text-[16px] inline-flex items-center gap-2">
            <Send size={14} className="text-accent" /> {kind === "depot" ? "Depot day" : "Assign task"}
          </h2>
          <p className="text-[12px] text-text-muted mt-1">
            {pinned
              ? <>For <span className="text-text font-medium">{assigneeName ?? "this employee"}</span>. They'll get an in-app bell notification.</>
              : <>Pick one person or several — each gets their own copy and their own bell notification.</>}
          </p>
        </div>

        <div className="p-4 space-y-3">
          {/* Mig 356 — what kind of day is this? The option James asked for:
              "the option to add an ADHOC or a Depot — currently just ADHOC." */}
          <div className="inline-flex rounded-md border border-line overflow-hidden">
            <button type="button" onClick={() => pickKind("task")}
              className={`px-3 py-1.5 text-[12px] font-medium transition-colors ${
                kind === "task" ? "bg-accent/15 text-accent" : "text-text-muted hover:text-text"}`}>
              Ad-hoc task
            </button>
            <button type="button" onClick={() => pickKind("depot")}
              className={`px-3 py-1.5 text-[12px] font-medium transition-colors border-l border-line ${
                kind === "depot" ? "bg-accent/15 text-accent" : "text-text-muted hover:text-text"}`}>
              Depot day
            </button>
          </div>
          {!pinned && (
            <Field label={`Assignees * ${pickedIds.size > 0 ? `— ${pickedIds.size} selected` : ""}`}>
              {empLoading ? (
                <div className="text-[12px] text-text-muted py-2"><Spinner size={12}/> Loading…</div>
              ) : (
                <div className="max-h-[180px] overflow-y-auto rounded border border-line divide-y divide-line/40">
                  {employees.map((e) => {
                    const on = pickedIds.has(e.employee_id);
                    return (
                      <label key={e.employee_id}
                        className={`flex items-center gap-2.5 px-3 py-1.5 cursor-pointer transition-colors ${
                          on ? "bg-accent/10" : "hover:bg-ink-hover"}`}>
                        <input type="checkbox" checked={on}
                          onChange={() => togglePicked(e.employee_id)} />
                        <span className="text-[13px]">{e.first_name} {e.last_name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </Field>
          )}

          <Field label="Title *">
            <Input
              value={title}
              onChange={(e: any) => setTitle(e.target.value)}
              placeholder="e.g. Drop plant at depot, Site visit Yarwun"
              maxLength={200}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Start *">
              <Input
                type="datetime-local"
                value={scheduledStart}
                onChange={(e: any) => setScheduledStart(e.target.value)}
              />
            </Field>
            <Field label="End (optional)">
              <Input
                type="datetime-local"
                value={scheduledEnd}
                onChange={(e: any) => setScheduledEnd(e.target.value)}
              />
            </Field>
          </div>

          <Field label="Location address (optional)">
            <Input
              value={locationAddress}
              onChange={(e: any) => setLocationAddress(e.target.value)}
              placeholder="e.g. Coretel depot, 12 Smith St Yarwun"
            />
          </Field>

          {/* A depot day is by definition not on a job, so the picker leaves
              rather than sitting there disabled and inviting a wrong answer. */}
          {kind !== "depot" && (
            <Field label="Project (optional)">
              <Select value={projectId} onChange={(e: any) => setProjectId(e.target.value)}>
                <option value="">— No project —</option>
                {projects.map((p) => (
                  <option key={p.project_id} value={p.project_id}>
                    {p.project_code} — {p.project_name}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <Field label="Notes (optional)">
            <Textarea
              rows={3}
              value={notes}
              onChange={(e: any) => setNotes(e.target.value)}
              placeholder="Anything extra the assignee needs to know — contact name, gate access, what to bring."
            />
          </Field>

          {err && (
            <div className="text-[12px] text-danger border border-danger/30 bg-danger/[0.05] rounded px-3 py-2 inline-flex items-start gap-2">
              <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" /> {err}
            </div>
          )}
        </div>

        <div className="p-3 border-t border-line bg-ink-raised flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={createMut.isPending}>
            Cancel
          </Button>
          <Button
            icon={Send}
            size="sm"
            onClick={() => createMut.mutate()}
            disabled={!canSubmit}
          >
            {createMut.isPending
              ? <><Spinner size={12} /> Assigning…</>
              : resolvedAssigneeIds.length > 1
                ? `${kind === "depot" ? "Depot day" : "Assign"} — ${resolvedAssigneeIds.length} people`
                : kind === "depot" ? "Add depot day" : "Assign task"}
          </Button>
        </div>
      </div>
    </div>
  );
}